
import { useState, FormEvent } from 'react';

export default function SignupForm({ id }: { id?: string }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Validate textarea length
    const notes = formData.get('notes') as string;
    if (notes && notes.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d64sqls5l058qvghfod0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id={id} className="py-12 bg-gray-50">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Request a sample issue
          </h2>
          <p className="text-base text-gray-600 mb-1">
            Tell us about your facility. We&apos;ll confirm next steps within 24 hours.
          </p>
          <p className="text-sm text-gray-500">
            No facility budget required. No PHI collected.
          </p>
        </div>
        <form 
          id="ltc-signup-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg p-6 shadow-xl border border-gray-200 relative"
        >
          <div className="relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              <div>
                <label htmlFor="facility-name" className="block text-sm font-semibold text-gray-900 mb-1.5">
                  Facility name *
                </label>
                <input 
                  id="facility-name"
                  name="facility_name"
                  required
                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all bg-white"
                />
              </div>
              <div>
                <label htmlFor="zip-code" className="block text-sm font-semibold text-gray-900 mb-1.5">
                  ZIP code *
                </label>
                <input 
                  id="zip-code"
                  name="zip_code"
                  required
                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all bg-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-semibold text-gray-900 mb-1.5">
                  Contact name *
                </label>
                <input 
                  id="contact-name"
                  name="contact_name"
                  required
                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all bg-white"
                />
              </div>
              <div>
                <label htmlFor="role" className="block text-sm font-semibold text-gray-900 mb-1.5">
                  Role / Title (optional)
                </label>
                <input 
                  id="role"
                  name="role"
                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all bg-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-1.5">
                  Work email *
                </label>
                <input 
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all bg-white"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-1.5">
                  Phone *
                </label>
                <input 
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all bg-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              <div>
                <label htmlFor="facility-type" className="block text-sm font-semibold text-gray-900 mb-1.5">
                  Facility type (optional)
                </label>
                <select 
                  id="facility-type"
                  name="facility_type"
                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all cursor-pointer bg-white"
                >
                  <option value="">Select facility type</option>
                  <option value="Skilled Nursing Facility (SNF)">Skilled Nursing Facility (SNF)</option>
                  <option value="Assisted Living">Assisted Living</option>
                  <option value="Memory Care">Memory Care</option>
                  <option value="Continuing Care / CCRC">Continuing Care / CCRC</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label htmlFor="bed-count" className="block text-sm font-semibold text-gray-900 mb-1.5">
                  Approx. bed count (optional)
                </label>
                <select 
                  id="bed-count"
                  name="bed_count"
                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all cursor-pointer bg-white"
                >
                  <option value="">Select range</option>
                  <option value="Under 50">Under 50</option>
                  <option value="50–99">50–99</option>
                  <option value="100–199">100–199</option>
                  <option value="200+">200+</option>
                </select>
              </div>
            </div>

            <div className="mb-5">
              <label htmlFor="notes" className="block text-sm font-semibold text-gray-900 mb-1.5">
                Notes (optional)
              </label>
              <textarea 
                id="notes"
                name="notes"
                rows={3}
                maxLength={500}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all resize-none bg-white"
              />
              <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
            </div>

            {submitStatus === 'success' && (
              <div className="mb-5 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">Thank you! Your request has been submitted successfully.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">Something went wrong. Please try again.</p>
              </div>
            )}

            <button 
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-[#6B4EFF] to-[#FF4D7D] text-white px-8 py-3.5 rounded-lg text-base font-semibold hover:from-[#5A3EEE] hover:to-[#EE3C6C] transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Submitting...' : 'Request a sample issue'}
            </button>
            <p className="text-xs text-gray-500 text-center mt-2">
              No obligation. We&apos;ll send a sample first.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
}
